import React, { useState, useRef, useCallback } from "react";
import { validateSTRN, scoreSignals, analyseFBRQR, extractFromText } from "../utils";
import { useFBRLookup } from "../hooks/useLookupNTN";
import { Disclaimer } from "../components/Disclaimer";
import { ResultCard } from "../components/ResultCard";

export function UploadPage({ t }) {
  const [file,      setFile]      = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [stage,     setStage]     = useState("idle");
  const [qrResult,  setQrResult]  = useState(null);
  const [ocrData,   setOcrData]   = useState(null);
  const [ocrRaw,    setOcrRaw]    = useState("");
  const [result,    setResult]    = useState(null);
  const [dragover,  setDragover]  = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [stageLabel,  setStageLabel]  = useState("");
  const fileRef  = useRef();
  const canvasRef= useRef();
  const imgRef   = useRef();
  const { lookupNTN } = useFBRLookup();

  const reset = () => { setFile(null); setPreview(null); setStage("idle"); setQrResult(null); setOcrData(null); setOcrRaw(""); setResult(null); setOcrProgress(0); };

  const handleFile = (f) => {
    if (!f) return;
    if (!["image/jpeg","image/png","image/webp","application/pdf"].includes(f.type)) { alert("Please upload JPG, PNG, WebP, or PDF."); return; }
    if (f.size > 10*1024*1024) { alert("Max 10MB."); return; }
    reset(); setFile(f);
    if (f.type !== "application/pdf") { const r = new FileReader(); r.onload = e => setPreview(e.target.result); r.readAsDataURL(f); }
  };

  const detectQR = useCallback((imgEl) => new Promise(resolve => {
    if (!window.jsQR) { resolve(null); return; }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = imgEl.naturalWidth; canvas.height = imgEl.naturalHeight;
    ctx.drawImage(imgEl, 0, 0);
    const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = window.jsQR(id.data, id.width, id.height, { inversionAttempts:"dontInvert" });
    resolve(code ? code.data : null);
  }), []);

  const processImage = useCallback(async () => {
    if (!file || !preview) return;
    setStage("loading"); setStageLabel("Loading QR detector...");
    if (!window.jsQR) {
      await new Promise((res, rej) => { const s = document.createElement("script"); s.src = "https://cdnjs.cloudflare.com/ajax/libs/jsqr/1.4.0/jsQR.min.js"; s.onload = res; s.onerror = rej; document.head.appendChild(s); }).catch(() => {});
    }
    setStage("qr"); setStageLabel("Scanning for QR codes...");
    await new Promise(r => setTimeout(r, 300));
    let qrData = null;
    if (imgRef.current && window.jsQR) qrData = await detectQR(imgRef.current);
    const qrAnalysis = analyseFBRQR(qrData);
    setQrResult(qrAnalysis);

    setStage("ocr"); setStageLabel("Running OCR text extraction...");
    setOcrProgress(0);
    let extractedData = null, rawText = "";
    try {
      if (!window.Tesseract) {
        await new Promise((res, rej) => { const s = document.createElement("script"); s.src = "https://cdnjs.cloudflare.com/ajax/libs/tesseract.js/4.1.4/tesseract.min.js"; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
      }
      const r2 = await window.Tesseract.recognize(preview, "eng", { logger: m => { if (m.status === "recognizing text") setOcrProgress(Math.round(m.progress * 100)); } });
      rawText = r2.data.text;
      extractedData = extractFromText(rawText);
    } catch(e) { rawText = "OCR could not be completed: " + e.message; }
    setOcrRaw(rawText); setOcrData(extractedData);

    const ntnRaw = qrAnalysis?.extractedNTN || extractedData?.sntn || extractedData?.ntn || null;
    const ntnLookup = ntnRaw ? await lookupNTN(ntnRaw) : null;
    const ocrNTNLookup = extractedData?.ntn ? await lookupNTN(extractedData.ntn) : null;
    const scored = scoreSignals({
      ntnLookup,
      strnResult: (qrAnalysis?.extractedSTRN || extractedData?.strn) ? validateSTRN(qrAnalysis?.extractedSTRN || extractedData?.strn) : null,
      hasQR: !!qrData, qrAnalysis,
      hasTaxClaimed: extractedData?.hasTaxClaimed || false,
      ocrNTNLookup,
    });
    setResult(scored); setStage("done"); setStageLabel("");
  }, [file, preview, detectQR, lookupNTN]);

  return (
    <div className="container">
      <div className="page-header">
        <h2 className="page-title">{t("upload_title")}</h2>
        <p className="page-sub">{t("upload_sub")}</p>
      </div>
      <Disclaimer t={t} />
      <canvas ref={canvasRef} style={{ display:"none" }} />

      {stage === "idle" && !file && (
        <div className={`upload-zone ${dragover ? "dragover" : ""}`}
          onClick={() => fileRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDragover(true); }}
          onDragLeave={() => setDragover(false)}
          onDrop={e => { e.preventDefault(); setDragover(false); handleFile(e.dataTransfer.files[0]); }}>
          <input type="file" ref={fileRef} accept="image/*,.pdf" style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])} />
          <div className="upload-icon">📄</div>
          <div className="upload-title">Drop your receipt here</div>
          <div className="upload-sub">or click to browse · JPG, PNG, WebP, PDF · max 10MB</div>
          <div className="upload-hint">Works for restaurant bills, shop receipts, pharmacy slips, hotel invoices, and more</div>
        </div>
      )}

      {file && stage === "idle" && (
        <div className="preview-card">
          {preview && <img ref={imgRef} src={preview} alt="Receipt preview" className="preview-img" crossOrigin="anonymous" />}
          <div className="preview-name">📄 {file.name} ({(file.size/1024).toFixed(0)} KB)</div>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <button className="btn btn-primary" onClick={processImage}>🔍 Scan & Verify</button>
            <button className="btn btn-ghost btn-sm" onClick={reset}>✕ Remove</button>
          </div>
        </div>
      )}

      {preview && stage !== "idle" && <img ref={imgRef} src={preview} alt="" style={{ display:"none" }} crossOrigin="anonymous" />}

      {(stage === "loading" || stage === "qr" || stage === "ocr") && (
        <div className="progress-card">
          <div className="progress-spinner">⏳</div>
          <div className="progress-label">{stageLabel}</div>
          {stage === "ocr" && (
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width:`${ocrProgress}%` }} />
              <div className="progress-bar-text">{ocrProgress}%</div>
            </div>
          )}
          <div className="progress-steps">
            {["Load tools","QR scan","OCR text","Score"].map((s,i) => {
              const si = {"loading":0,"qr":1,"ocr":2,"done":3}[stage];
              return <div key={s} className={`pstep ${i < si ? "pstep-done" : i === si ? "pstep-active" : ""}`}>{i+1}. {s}</div>;
            })}
          </div>
        </div>
      )}

      {stage === "done" && (
        <>
          <div className="scan-section">
            <div className="scan-section-title">QR Code Scan</div>
            {qrResult ? (
              <div className={`scan-result ${qrResult.isFBRPOS ? "scan-ok" : qrResult.isProvincial ? "scan-ok" : "scan-warn"}`}>
                {qrResult.isFBRPOS && <><strong>✅ FBR POS QR Code detected</strong> — this receipt is from an FBR-registered POS machine.{qrResult.posInvoiceNum && <> POS Invoice: <code>{qrResult.posInvoiceNum}</code></>}</>}
                {qrResult.isProvincial && !qrResult.isFBRPOS && <><strong>✅ Provincial authority QR code detected</strong></>}
                {!qrResult.isFBRPOS && !qrResult.isProvincial && <><strong>⚠️ QR found but not from FBR/provincial authority</strong><br/><code style={{fontSize:11}}>{qrResult.raw?.slice(0,80)}</code></>}
              </div>
            ) : (
              <div className="scan-result scan-neutral">No QR code detected. FBR-registered Tier-1 businesses are required to print a POS QR code on every bill.</div>
            )}
          </div>

          <div className="scan-section">
            <div className="scan-section-title">OCR Extraction Results</div>
            {ocrData && (
              <div className="ocr-grid">
                {[
                  ["NTN Found",   ocrData.ntn  || "—"],
                  ["SNTN Found",  ocrData.sntn || "—"],
                  ["STRN Found",  ocrData.strn || "—"],
                  ["Tax Lines",   ocrData.taxLines.length ? ocrData.taxLines.join(" · ") : "None detected"],
                  ["Tax Claimed", ocrData.hasTaxClaimed ? "Yes" : "No"],
                  ["Bill Total",  ocrData.total ? `PKR ${ocrData.total}` : "—"],
                ].map(([k,v]) => (
                  <div key={k} className="ocr-row"><span className="ocr-key">{k}</span><span className="ocr-val">{v}</span></div>
                ))}
              </div>
            )}
            {ocrRaw && (
              <details className="ocr-raw-details">
                <summary>View raw OCR text</summary>
                <pre className="ocr-raw">{ocrRaw.slice(0,1200)}{ocrRaw.length>1200?"\n…[truncated]":""}</pre>
              </details>
            )}
          </div>

          {result && <ResultCard result={result} ntn={qrResult?.extractedNTN||ocrData?.ntn||null} strn={qrResult?.extractedSTRN||ocrData?.strn||null} qrAnalysis={qrResult} t={t} />}
          <button className="btn btn-ghost btn-sm" style={{ marginTop:16 }} onClick={reset}>← Scan Another Receipt</button>
        </>
      )}

      <div className="info-card" style={{ marginTop:24 }}>
        <div className="section-title" style={{ fontSize:14, marginBottom:10 }}>What makes any bill legitimate?</div>
        <div className="check-list">
          {[
            ["yes","NTN or SNTN printed clearly on the bill and found in FBR's database"],
            ["yes","FBR POS QR code present (mandatory for Tier-1 businesses since 2019)"],
            ["yes","Tax type and rate explicitly stated (e.g. 'GST @ 17%' or 'Service Tax @ 16%')"],
            ["yes","Unique invoice/receipt serial number"],
            ["no", "Only the word 'Tax' or 'GST' with no registration number"],
            ["no", "NTN not found in FBR Active Taxpayer List"],
            ["no", "Handwritten tax addition on a printed bill"],
          ].map(([t_type,text]) => (
            <div key={text} className={`check-item check-${t_type === "yes" ? "yes" : "no"}`}>{t_type === "yes" ? "✅" : "❌"} {text}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
